import { useScrollReveal } from '@/hooks/useScrollReveal';
import { useRef, useState, useCallback, useEffect } from 'react';
import { MessageSquare, Filter, Bot, Brain, Cpu, Send, Sparkles, Hand } from 'lucide-react';

/* ─── Shared data ─── */

const nodeDefinitions = [
  { id: 'whatsapp', icon: MessageSquare, label: 'WhatsApp', sub: 'Messages entrants', color: 'emerald', x: 4, y: 34, mobileX: 1, mobileY: 34, ports: { right: true } },
  { id: 'filter', icon: Filter, label: 'Filtre', sub: 'Tri intelligent', color: 'blue', x: 22, y: 34, mobileX: 35, mobileY: 34, ports: { left: true, right: true } },
  { id: 'agent', icon: Bot, label: 'Agent IA', sub: 'FastTask Engine', color: 'dark', x: 42, y: 20, mobileX: 37, mobileY: 3, ports: { left: true, right: true, bottom: true } },
  { id: 'memory', icon: Brain, label: 'Mémoire', sub: 'Historique client', color: 'amber', x: 30, y: 68, mobileX: 14, mobileY: 66, ports: { right: true, top: true } },
  { id: 'openai', icon: Cpu, label: 'OpenAI', sub: 'Chat Model', color: 'violet', x: 52, y: 68, mobileX: 52, mobileY: 66, ports: { left: true, top: true } },
  { id: 'reply', icon: Send, label: 'Réponse', sub: 'Message client', color: 'emerald', x: 72, y: 34, mobileX: 70, mobileY: 34, ports: { left: true } },
];

const connectionDefs = [
  { from: 'whatsapp', fromPort: 'right', to: 'filter', toPort: 'left', active: true },
  { from: 'filter', fromPort: 'right', to: 'agent', toPort: 'left', active: true },
  { from: 'agent', fromPort: 'right', to: 'reply', toPort: 'left', active: true },
  { from: 'memory', fromPort: 'right', to: 'openai', toPort: 'left', active: false },
  { from: 'agent', fromPort: 'bottom', to: 'memory', toPort: 'top', active: false },
  { from: 'agent', fromPort: 'bottom', to: 'openai', toPort: 'top', active: false },
];

const colorMap = {
  emerald: {
    bg: 'bg-emerald-500/10 dark:bg-emerald-400/10',
    text: 'text-emerald-600 dark:text-emerald-400',
    glow: 'group-hover:shadow-emerald-500/10',
  },
  blue: {
    bg: 'bg-[hsl(211,100%,50%)]/10 dark:bg-[hsl(211,100%,55%)]/10',
    text: 'text-[hsl(211,100%,50%)] dark:text-[hsl(211,100%,55%)]',
    glow: 'group-hover:shadow-blue-500/10',
  },
  dark: {
    bg: 'bg-foreground/10 dark:bg-foreground/10',
    text: 'text-foreground dark:text-foreground',
    glow: 'group-hover:shadow-foreground/5',
  },
  amber: {
    bg: 'bg-amber-500/10 dark:bg-amber-400/10',
    text: 'text-amber-600 dark:text-amber-400',
    glow: 'group-hover:shadow-amber-500/10',
  },
  violet: {
    bg: 'bg-violet-500/10 dark:bg-violet-400/10',
    text: 'text-violet-600 dark:text-violet-400',
    glow: 'group-hover:shadow-violet-500/10',
  },
};

/* ─── Responsive sizes ─── */
const DESKTOP_W = 130;
const DESKTOP_H = 95;
const MOBILE_W = 88;
const MOBILE_H = 72;

function getPortPosition(pos, port, nodeW, nodeH) {
  switch (port) {
    case 'right': return { x: pos.x + nodeW, y: pos.y + nodeH / 2 };
    case 'left': return { x: pos.x, y: pos.y + nodeH / 2 };
    case 'bottom': return { x: pos.x + nodeW / 2, y: pos.y + nodeH };
    case 'top': return { x: pos.x + nodeW / 2, y: pos.y };
    default: return { x: pos.x + nodeW / 2, y: pos.y + nodeH / 2 };
  }
}

function cubicPath(a, b) {
  const dx = Math.abs(b.x - a.x) * 0.5;
  const dy = Math.abs(b.y - a.y) * 0.3;
  if (Math.abs(a.y - b.y) > Math.abs(a.x - b.x)) {
    return `M${a.x},${a.y} C${a.x},${a.y + dy} ${b.x},${b.y - dy} ${b.x},${b.y}`;
  }
  return `M${a.x},${a.y} C${a.x + dx},${a.y} ${b.x - dx},${b.y} ${b.x},${b.y}`;
}

/* ─── Draggable node ─── */

function WorkflowNode({ node, position, onDrag, index, isMobile, isActive }) {
  const colors = colorMap[node.color];
  const Icon = node.icon;
  const nodeRef = useRef(null);
  const isDark = node.color === 'dark';
  const nodeW = isMobile ? MOBILE_W : DESKTOP_W;
  const nodeH = isMobile ? MOBILE_H : DESKTOP_H;

  const handlePointerDown = useCallback((e) => {
    if (!isActive) return;
    e.preventDefault();
    e.stopPropagation();
    const el = nodeRef.current;
    if (!el) return;
    const container = el.closest('.workflow-canvas');
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const startX = e.clientX;
    const startY = e.clientY;
    const startLeft = position.x;
    const startTop = position.y;

    const handleMove = (ev) => {
      const dx = ev.clientX - startX;
      const dy = ev.clientY - startY;
      const newX = Math.max(0, Math.min(rect.width - nodeW, startLeft + dx));
      const newY = Math.max(0, Math.min(rect.height - nodeH, startTop + dy));
      onDrag(node.id, newX, newY);
    };

    const handleUp = () => {
      document.removeEventListener('pointermove', handleMove);
      document.removeEventListener('pointerup', handleUp);
    };

    document.addEventListener('pointermove', handleMove);
    document.addEventListener('pointerup', handleUp);
  }, [node.id, position, onDrag, isActive, nodeW, nodeH]);

  return (
    <div
      ref={nodeRef}
      className={`workflow-node group absolute select-none ${isActive ? 'touch-none' : 'touch-auto pointer-events-none'}`}
      style={{
        left: position.x,
        top: position.y,
        zIndex: 5,
        animationDelay: `${index * 0.1 + 0.3}s`,
      }}
      onPointerDown={handlePointerDown}
    >
      <div
        className={`
          relative flex flex-col items-center gap-1 md:gap-1.5 p-2 md:p-3 rounded-xl md:rounded-2xl border backdrop-blur-sm
          transition-all duration-300 ease-out
          ${isActive ? 'cursor-grab active:cursor-grabbing' : ''}
          ${isDark
            ? 'bg-[#1E293B] border-[#334155] shadow-lg'
            : 'bg-card/80 dark:bg-card/60 border-border/50 hover:border-[hsl(211,100%,50%)]/30 shadow-sm hover:shadow-lg'
          }
          ${colors.glow}
        `}
        style={{ minWidth: nodeW }}
      >
        {node.ports.left && (
          <div className="workflow-port absolute left-0 top-1/2 -translate-x-1/2 -translate-y-1/2 w-2 h-2 md:w-2.5 md:h-2.5 rounded-full bg-background border-2 border-border/70 group-hover:border-[hsl(211,100%,50%)] transition-colors duration-200" />
        )}
        {node.ports.right && (
          <div className="workflow-port absolute right-0 top-1/2 translate-x-1/2 -translate-y-1/2 w-2 h-2 md:w-2.5 md:h-2.5 rounded-full bg-background border-2 border-border/70 group-hover:border-[hsl(211,100%,50%)] transition-colors duration-200" />
        )}
        {node.ports.bottom && (
          <div className="workflow-port absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 w-2 h-2 md:w-2.5 md:h-2.5 rounded-full bg-background border-2 border-border/70 group-hover:border-[hsl(211,100%,50%)] transition-colors duration-200" />
        )}
        {node.ports.top && (
          <div className="workflow-port absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 w-2 h-2 md:w-2.5 md:h-2.5 rounded-full bg-background border-2 border-border/70 group-hover:border-[hsl(211,100%,50%)] transition-colors duration-200" />
        )}

        <div className={`w-8 h-8 md:w-10 md:h-10 rounded-lg md:rounded-xl flex items-center justify-center ${isDark ? 'bg-white/10' : colors.bg}`}>
          <Icon className={`w-4 h-4 md:w-5 md:h-5 ${isDark ? 'text-white' : colors.text}`} />
        </div>
        <span className={`text-[9px] md:text-[11px] font-bold tracking-tight leading-tight ${isDark ? 'text-white' : 'text-foreground'}`}>
          {node.label}
        </span>
        <span className={`text-[7px] md:text-[9px] font-medium leading-tight ${isDark ? 'text-slate-400' : 'text-muted-foreground'} ${isMobile ? 'hidden' : ''}`}>
          {node.sub}
        </span>
      </div>
    </div>
  );
}

/* ─── Interactive Canvas (shared mobile + desktop) ─── */

function InteractiveCanvas({ isMobile }) {
  const canvasRef = useScrollReveal();
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const [positions, setPositions] = useState({});
  const [isReady, setIsReady] = useState(false);
  const [isActive, setIsActive] = useState(!isMobile);

  const nodeW = isMobile ? MOBILE_W : DESKTOP_W;
  const nodeH = isMobile ? MOBILE_H : DESKTOP_H;

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const ro = new ResizeObserver((entries) => {
      const { width, height } = entries[0].contentRect;
      const newPositions = {};
      nodeDefinitions.forEach((n) => {
        const px = isMobile ? n.mobileX : n.x;
        const py = isMobile ? n.mobileY : n.y;
        newPositions[n.id] = { x: (px / 100) * width, y: (py / 100) * height };
      });
      setPositions(newPositions);
      setIsReady(true);
    });
    ro.observe(container);
    return () => ro.disconnect();
  }, [isMobile]);

  const handleDrag = useCallback((id, x, y) => {
    setPositions((prev) => ({ ...prev, [id]: { x, y } }));
  }, []);

  const handleActivate = useCallback(() => {
    if (!isActive) setIsActive(true);
  }, [isActive]);

  // SVG connections
  useEffect(() => {
    if (!svgRef.current || !isReady) return;
    const svg = svgRef.current;
    while (svg.firstChild) svg.removeChild(svg.firstChild);

    const defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs');
    ['active', 'inactive'].forEach((type) => {
      const marker = document.createElementNS('http://www.w3.org/2000/svg', 'marker');
      marker.setAttribute('id', `wf-arrow-${type}${isMobile ? '-m' : ''}`);
      marker.setAttribute('markerWidth', '8');
      marker.setAttribute('markerHeight', '8');
      marker.setAttribute('refX', '6');
      marker.setAttribute('refY', '3');
      marker.setAttribute('orient', 'auto');
      const p = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      p.setAttribute('d', 'M0,0 L0,6 L8,3 z');
      p.setAttribute('class', `workflow-arrow-${type}`);
      marker.appendChild(p);
      defs.appendChild(marker);
    });
    svg.appendChild(defs);

    connectionDefs.forEach((conn) => {
      const fromPos = positions[conn.from];
      const toPos = positions[conn.to];
      if (!fromPos || !toPos) return;
      const a = getPortPosition(fromPos, conn.fromPort, nodeW, nodeH);
      const b = getPortPosition(toPos, conn.toPort, nodeW, nodeH);
      const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      path.setAttribute('d', cubicPath(a, b));
      path.setAttribute('class', `workflow-conn ${conn.active ? 'workflow-conn-active' : 'workflow-conn-inactive'}`);
      path.setAttribute('marker-end', conn.active ? `url(#wf-arrow-active${isMobile ? '-m' : ''})` : `url(#wf-arrow-inactive${isMobile ? '-m' : ''})`);
      svg.appendChild(path);
    });
  }, [positions, isReady, nodeW, nodeH, isMobile]);

  return (
    <div
      ref={(el) => { canvasRef.current = el; containerRef.current = el; }}
      className="workflow-canvas scroll-reveal relative w-full overflow-hidden rounded-3xl border border-border/50 bg-card/50 dark:bg-card/30 backdrop-blur-sm"
      style={{ height: isMobile ? 'clamp(320px, 85vw, 400px)' : 'clamp(420px, 42vw, 520px)' }}
      onClick={handleActivate}
      onTouchStart={handleActivate}
    >
      {/* Dot grid */}
      <div className="absolute inset-0 workflow-dot-grid pointer-events-none" />

      {/* Active badge */}
      <div className="absolute top-3 md:top-4 left-3 md:left-4 z-10 flex items-center gap-1.5 md:gap-2 px-2.5 md:px-3 py-1 md:py-1.5 rounded-full bg-emerald-500/10 dark:bg-emerald-400/10 border border-emerald-500/20 dark:border-emerald-400/20">
        <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 dark:bg-emerald-400 workflow-pulse-dot" />
        <span className="text-[9px] md:text-[10px] font-semibold tracking-wider uppercase text-emerald-600 dark:text-emerald-400">Actif</span>
      </div>

      {/* SVG connections */}
      <svg ref={svgRef} className="absolute inset-0 w-full h-full pointer-events-none" style={{ zIndex: 1 }} />

      {/* Nodes */}
      {isReady && nodeDefinitions.map((node, i) => (
        <WorkflowNode
          key={node.id}
          node={node}
          position={positions[node.id]}
          onDrag={handleDrag}
          index={i}
          isMobile={isMobile}
          isActive={isActive}
        />
      ))}

      {/* Mobile: Tap-to-interact overlay */}
      {isMobile && !isActive && (
        <div
          className="wf-tap-overlay absolute inset-0 z-30 flex flex-col items-center justify-center gap-3 rounded-3xl"
          role="button"
          tabIndex={0}
          aria-label="Touchez pour interagir avec le workflow"
        >
          {/* Frosted glass layer */}
          <div className="absolute inset-0 bg-background/40 dark:bg-background/50 backdrop-blur-[2px] rounded-3xl" />

          {/* Content */}
          <div className="relative z-10 flex flex-col items-center gap-2.5">
            <div className="wf-tap-ring w-14 h-14 rounded-full border-2 border-[hsl(211,100%,50%)]/30 dark:border-[hsl(211,100%,55%)]/30 flex items-center justify-center">
              <div className="w-10 h-10 rounded-full bg-[hsl(211,100%,50%)]/10 dark:bg-[hsl(211,100%,55%)]/10 flex items-center justify-center">
                <Hand className="w-5 h-5 text-[hsl(211,100%,50%)] dark:text-[hsl(211,100%,55%)] wf-tap-hand" />
              </div>
            </div>
            <span className="text-[11px] font-semibold tracking-wide text-foreground/70">
              Touchez pour interagir
            </span>
          </div>
        </div>
      )}

      {/* Desktop hint */}
      {!isMobile && (
        <p className="absolute bottom-3 right-4 text-[10px] text-muted-foreground/60 font-medium tracking-wide pointer-events-none z-10">
          <Sparkles className="w-3 h-3 inline mr-1 opacity-50" />
          Glissez les blocs pour les déplacer
        </p>
      )}
    </div>
  );
}

/* ─── Main section ─── */

export default function WorkflowSection() {
  const titleRef = useScrollReveal();
  const ctaRef = useScrollReveal();
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    setIsMobile(mq.matches);
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  return (
    <section id="workflow" className="py-24 md:py-32">
      <div className="container mx-auto px-6 md:px-12 max-w-7xl">
        {/* Header */}
        <div ref={titleRef} className="scroll-reveal text-center mb-12 md:mb-16">
          <p className="text-sm font-semibold tracking-[0.2em] uppercase text-[hsl(211,100%,50%)] dark:text-[hsl(211,100%,55%)] mb-4">
            Comment ça fonctionne
          </p>
          <h2 className="text-3xl md:text-5xl font-semibold tracking-tight text-foreground mb-6">
            Votre workflow IA — en temps réel
          </h2>
          <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            Chaque message est capté, analysé et traité automatiquement.
            <span className="hidden md:inline"> Déplacez les blocs pour explorer le flux.</span>
          </p>
        </div>

        {/* Interactive Canvas — responsive */}
        <InteractiveCanvas isMobile={isMobile} />

        {/* CTA */}
        <div ref={ctaRef} className="scroll-reveal text-center mt-10">
          <a
            href="#audit"
            onClick={(e) => {
              e.preventDefault();
              document.getElementById('audit')?.scrollIntoView({ behavior: 'smooth' });
            }}
            className="btn-premium inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-[hsl(211,100%,50%)] dark:bg-[hsl(211,100%,55%)] text-white font-medium text-sm"
          >
            Automatiser mon activité
            <Sparkles className="w-4 h-4" />
          </a>
        </div>
      </div>
    </section>
  );
}
